import { cn } from '@/utils/styles/cn'
import { IChildrenProps } from '@/utils/typings/children-props'
import { AnimatePresence, motion, useIsPresent, useWillChange } from 'framer-motion'
import { forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react'
import { useMeasure } from 'react-use'

interface IDimensionsFadeContainerProps extends IChildrenProps {
    initialHeight?: number
    animateHeight?: boolean
    animateWidth?: boolean
    animateOpacity?: boolean
    /** When true, skips the initial mount animation (e.g. width fade-in). Animations on childrenKey change still run. */
    skipInitialAnimation?: boolean
    childrenKey?: string | number | boolean
    innerClassName?: string
    className?: string
    childrenClassName?: string
    maxHeight?: number
    maxWidth?: number
    wheelAnimation?: boolean
    opacityDuration?: number
    heightMass?: number
    heightInstant?: boolean
    /** When true, height *decreases* are instant while increases still animate. */
    heightShrinkInstant?: boolean
    animatePresenceMode?: 'wait' | 'popLayout' | 'sync'
    onDimensionsAnimationComplete?: () => void
    animateHeightInstant?: boolean
    widthTransition?: { mass?: number; stiffness?: number; damping?: number }
    heightTransition?: { mass?: number; stiffness?: number; damping?: number }
    yTransition?: { mass?: number; stiffness?: number; damping?: number }
}

interface IPresenceContentProps extends IChildrenProps {
    animateOpacity: boolean
    wheelAnimation: boolean
    opacityDuration?: number
    yTransition: { mass?: number; stiffness?: number; damping?: number }
    className?: string
    willChange: ReturnType<typeof useWillChange>
}

/** A pop-layout exit must not leave stale actions interactive or exposed to assistive technology. */
const PresenceContent = forwardRef<HTMLDivElement, IPresenceContentProps>(function PresenceContent(
    { children, animateOpacity, wheelAnimation, opacityDuration, yTransition, className, willChange },
    ref
) {
    const isPresent = useIsPresent()

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: animateOpacity ? 0 : 1, y: wheelAnimation ? 20 : 0 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{
                opacity: animateOpacity ? 0 : 1,
                y: wheelAnimation ? -20 : 0,
                transition: {
                    opacity: {
                        duration: wheelAnimation ? (opacityDuration ?? 0.2) : (opacityDuration ?? 0.15),
                        ease: 'easeInOut',
                    },
                    y: { type: 'spring', ...yTransition },
                },
            }}
            transition={{
                opacity: {
                    duration: wheelAnimation ? (opacityDuration ?? 0.35) : (opacityDuration ?? 0.15),
                    ease: 'easeInOut',
                },
                y: { type: 'spring', ...yTransition },
            }}
            className={className}
            aria-hidden={isPresent ? undefined : true}
            inert={isPresent ? undefined : true}
            style={{
                pointerEvents: isPresent ? undefined : 'none',
                willChange: wheelAnimation ? willChange : undefined,
            }}
        >
            {children}
        </motion.div>
    )
})

export const DimensionsFadeContainer = ({
    initialHeight,
    children,
    childrenKey,
    animateHeight = true,
    animateWidth = true,
    animateOpacity = true,
    skipInitialAnimation = false,
    className,
    innerClassName,
    childrenClassName,
    maxHeight,
    maxWidth,
    opacityDuration,
    heightMass,
    heightInstant = false,
    heightShrinkInstant = false,
    wheelAnimation = false,
    animatePresenceMode = wheelAnimation ? 'popLayout' : 'wait',
    onDimensionsAnimationComplete,
    widthTransition,
    heightTransition,
    yTransition = { stiffness: 200, damping: 25 },
}: IDimensionsFadeContainerProps) => {
    const componentId = useId()

    const willChange = useWillChange()

    const [canAnimate, setCanAnimate] = useState(skipInitialAnimation)

    useEffect(() => {
        if (skipInitialAnimation) return
        const timeout = setTimeout(() => setCanAnimate(true), 500)
        return () => clearTimeout(timeout)
    }, [skipInitialAnimation])

    const [measureRef, { width: innerWidth, height: innerHeight }] = useMeasure<HTMLDivElement>()

    const prevHeightRef = useRef(innerHeight)
    const isShrinking = heightShrinkInstant && innerHeight < prevHeightRef.current
    useEffect(() => {
        prevHeightRef.current = innerHeight
    }, [innerHeight])

    // Combine the measure ref with a patch that prevents removeChild crashes.
    // AnimatePresence keeps exiting children in the DOM during exit animations;
    // if useMeasure's ResizeObserver triggers a React re-render mid-animation,
    // React may try to removeChild a node that AnimatePresence already detached.
    const innerRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (node) {
                const original = node.removeChild
                node.removeChild = function <T extends Node>(child: T): T {
                    if (child.parentNode !== this) return child
                    return original.call(this, child) as T
                }

                measureRef(node)
            }
        },
        [measureRef]
    )

    const width = animateWidth
        ? canAnimate
            ? (() => {
                  const measured = typeof maxWidth === 'number' ? Math.min(maxWidth, innerWidth) : innerWidth
                  return measured && measured > 0 ? measured : 'fit-content'
              })()
            : 'fit-content'
        : undefined

    const height = animateHeight
        ? (canAnimate || typeof initialHeight === 'number') && !heightInstant
            ? typeof maxHeight === 'number'
                ? Math.min(maxHeight, innerHeight)
                : innerHeight
            : typeof initialHeight === 'number'
              ? 'auto'
              : 'fit-content'
        : undefined

    return (
        <motion.div
            initial={{
                width: animateWidth ? 'fit-content' : undefined,
                height: animateHeight ? (typeof initialHeight === 'number' ? initialHeight : 'fit-content') : undefined,
            }}
            animate={{ width, height }}
            transition={{
                width: { type: 'spring', mass: 0.3, ...widthTransition },
                height: isShrinking
                    ? { duration: 0 }
                    : { type: 'spring', mass: heightMass ?? 0.3, ...heightTransition },
            }}
            style={{ maxWidth, maxHeight }}
            className={cn('overflow-hidden', className)}
            onAnimationComplete={onDimensionsAnimationComplete}
        >
            <div
                ref={innerRef}
                className={cn(
                    animateHeight && 'min-h-fit',
                    animateWidth && 'min-w-fit max-w-fit whitespace-nowrap',
                    innerClassName
                )}
            >
                <AnimatePresence initial={false} mode={animatePresenceMode}>
                    <PresenceContent
                        animateOpacity={animateOpacity}
                        wheelAnimation={wheelAnimation}
                        opacityDuration={opacityDuration}
                        yTransition={yTransition}
                        className={childrenClassName}
                        willChange={willChange}
                        key={
                            'width-fade-container' +
                            componentId +
                            (typeof childrenKey !== 'undefined'
                                ? childrenKey
                                : typeof children === 'string'
                                  ? children
                                  : '')
                        }
                    >
                        {children}
                    </PresenceContent>
                </AnimatePresence>
            </div>
        </motion.div>
    )
}
